import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

export type CreditUsageBarProps = {
  /** Total credits in the current allowance period. */
  totalCredits: number;
  /** Credits already spent this period. */
  usedCredits: number;
  /** Authoritative remaining balance. Falls back to `totalCredits − usedCredits` when omitted. */
  remainingCredits?: number;
  /** Credits this action would consume (the "this export" segment). */
  marginalCredits: number;
  /** Human-formatted reset date, e.g. "1 October 2026". */
  resetDate?: string;
};

/**
 * Segmented "remaining credits" meter for the export flow.
 * - blue: credits still left before this action
 * - light blue: the tail of that balance this action will consume
 * - grey track: credits already spent
 * When the action would exceed what's remaining, the whole remaining segment turns
 * red (and consumers disable the action). Purely presentational — numeric props only.
 */
function CreditUsageBar({
  totalCredits,
  usedCredits,
  remainingCredits,
  marginalCredits,
  resetDate,
}: CreditUsageBarProps) {
  const remainingBefore = remainingCredits ?? Math.max(0, totalCredits - usedCredits);
  const overLimit = marginalCredits > remainingBefore;
  const pct = (n: number) =>
    totalCredits > 0 ? Math.max(0, Math.min(100, (n / totalCredits) * 100)) : 0;

  const remainingBeforePct = pct(remainingBefore);
  const marginalPct = overLimit ? 0 : pct(marginalCredits);

  return (
    <Flex column gap={8}>
      <Flex gap={8} alignItems="baseline" justifyContent="space-between">
        <Text fontWeight="500">Remaining credits</Text>
        <Text description color="sysTextPlaceholder">
          {remainingBefore.toLocaleString()} of {totalCredits.toLocaleString()}
        </Text>
      </Flex>
      <Track>
        {overLimit ? (
          <OverLimitSegment style={{ width: `${remainingBeforePct}%` }} />
        ) : (
          <>
            <RemainingSegment style={{ width: `${remainingBeforePct}%` }} />
            {marginalCredits > 0 && (
              <MarginalSegment
                style={{ right: `${100 - remainingBeforePct}%`, width: `${marginalPct}%` }}
              />
            )}
          </>
        )}
      </Track>
      {resetDate && (
        <Flex justifyContent="flex-end">
          <Text description color="sysTextPlaceholder">
            Resets {resetDate}
          </Text>
        </Flex>
      )}
    </Flex>
  );
}

const Track = styled.div(({ theme }) => ({
  position: "relative",
  width: "100%",
  height: 8,
  borderRadius: 16,
  overflow: "hidden",
  background: theme.colors.sysBackgroundAlternative,
}));

const Segment = styled.div({
  position: "absolute",
  top: 0,
  height: 8,
  transition: "width 0.1s ease, left 0.1s ease",
});

const RemainingSegment = styled(Segment)(({ theme }) => ({
  left: 0,
  background: theme.colors.sysPrimaryDefault,
}));

// Sits at the end of the remaining balance and grows leftward into it, separated by a hairline
// of the panel's own background so the two read as distinct. Anchored on the right, and floored
// at 8px, because a typical export is a rounding error against the allowance: positioned from
// the left it would render sub-pixel, or be pushed past the end of the track and clipped away.
const MarginalSegment = styled(Segment)(({ theme }) => ({
  minWidth: 8,
  background: theme.colors.sysPrimarySubtleAlt,
  borderLeft: `1px solid ${theme.colors.sysBackgroundDefault}`,
  borderRight: `1px solid ${theme.colors.sysBackgroundDefault}`,
}));

const OverLimitSegment = styled(Segment)(({ theme }) => ({
  left: 0,
  background: theme.colors.sysDestructiveDefault,
}));

export default CreditUsageBar;
